import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Clock, User, Calendar, ArrowRight, Home, Search } from 'lucide-react';
import CustomCursor from './CustomCursor';
import articlesData from './articles.json';

const BlogPage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = ['all', 'Guides', 'Stratégie', 'Actualités', 'Events'];

    const filteredArticles = articlesData.filter(article => {
        const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 overflow-hidden">
            <CustomCursor />

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');
        
        .article-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .article-card:hover {
          transform: translateY(-8px);
        }
        
        .search-glow {
          box-shadow: 0 0 0 3px rgba(220, 10, 45, 0.1);
          transition: all 0.3s;
        }
        
        .search-glow:focus {
          box-shadow: 0 0 0 3px rgba(220, 10, 45, 0.3);
        }
      `}</style>

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>

                    <div className="flex items-center gap-4">
                        <div className="relative w-10 h-10">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white pt-32 pb-20 px-6">
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-10 left-10 w-32 h-32 border-8 border-white rounded-full"></div>
                    <div className="absolute bottom-20 right-20 w-48 h-48 border-8 border-white rounded-full"></div>
                </div>

                <div className="max-w-6xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                            className="inline-block mb-8"
                        >
                            <div className="text-7xl">📚</div>
                        </motion.div>

                        <h1 className="text-5xl lg:text-7xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Blog <span className="text-yellow-400">PokeMovie</span>
                        </h1>

                        <p className="text-2xl text-red-100 mb-12 max-w-3xl mx-auto font-semibold">
                            Guides, stratégies et actualités pour devenir un Maître Pokémon
                        </p>

                        {/* Search Bar */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="max-w-2xl mx-auto"
                        >
                            <div className="relative">
                                <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un article..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-16 pr-6 py-5 rounded-2xl text-gray-900 font-semibold text-lg search-glow focus:outline-none"
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="sticky top-20 z-40 bg-white border-b-4 border-gray-200 shadow-lg">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {categories.map((cat) => (
                            <motion.button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-6 py-3 rounded-full font-bold transition-all ${
                                    selectedCategory === cat
                                        ? 'bg-red-600 text-white shadow-xl'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                {cat === 'all' ? 'Tous' : cat}
                            </motion.button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Articles Grid */}
            <section className="py-20 px-6">
                <div className="max-w-6xl mx-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selectedCategory + searchQuery}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="grid md:grid-cols-2 gap-8"
                        >
                            {filteredArticles.length === 0 ? (
                                <div className="col-span-2 text-center py-20">
                                    <div className="text-6xl mb-4">😕</div>
                                    <h3 className="text-2xl font-bold text-gray-700 mb-2">Aucun article trouvé</h3>
                                    <p className="text-gray-500">Essaye une autre recherche ou catégorie</p>
                                </div>
                            ) : (
                                filteredArticles.map((article, index) => (
                                    <motion.div
                                        key={article.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="article-card group"
                                    >
                                        <div className="bg-white rounded-3xl shadow-xl border-4 border-gray-100 overflow-hidden h-full flex flex-col">
                                            {/* Article Image */}
                                            <div className="relative h-64 overflow-hidden">
                                                <img
                                                    src={article.image}
                                                    alt={article.title}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                                {/* Category Badge */}
                                                <div className="absolute top-4 left-4">
                                                  <span className="px-4 py-2 rounded-full bg-red-600 text-white font-bold text-sm shadow-xl">
                                                    {article.category}
                                                  </span>
                                                </div>

                                                {/* Featured Badge */}
                                                {article.featured && (
                                                    <div className="absolute top-4 right-4">
                                                        <span className="px-4 py-2 rounded-full bg-yellow-400 text-gray-900 font-black text-sm shadow-xl">
                                                          ⭐ À LA UNE
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Article Content */}
                                            <div className="p-8 flex-1 flex flex-col">
                                                <h2 className="text-3xl font-black text-gray-900 mb-4 group-hover:text-red-600 transition-colors" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                    {article.title}
                                                </h2>

                                                <p className="text-lg text-gray-600 mb-6 leading-relaxed font-semibold flex-1">
                                                    {article.excerpt}
                                                </p>

                                                {/* Meta Info */}
                                                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500 font-semibold">
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{article.readTime} min</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4" />
                                                        <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR')}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4" />
                                                        <span>{article.author}</span>
                                                    </div>
                                                </div>

                                                {/* Tags */}
                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {article.tags.slice(0, 3).map(tag => (
                                                        <span key={tag} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
                                                          #{tag}
                                                        </span>
                                                    ))}
                                                </div>

                                                {/* Read More Button */}
                                                <motion.button
                                                    onClick={() => navigate(`/blog/${article.slug}`)}
                                                    className="flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-red-600 text-white font-black text-lg shadow-xl hover:bg-red-700 transition-all group-hover:shadow-2xl"
                                                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    Lire l'Article
                                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                                </motion.button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-6xl mb-6">🎮</div>
                        <h2 className="text-4xl lg:text-5xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Prêt à Rejoindre ?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8 font-semibold max-w-2xl mx-auto">
                            Plus de 850 dresseurs t'attendent sur PokeMovie ! Commence ton aventure dès maintenant.
                        </p>
                        <motion.button
                            onClick={() => navigate('/play')}
                            className="inline-flex items-center gap-4 px-10 py-5 rounded-2xl bg-white text-blue-600 font-black text-xl shadow-2xl hover:bg-yellow-400 hover:text-blue-700 transition-all"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -3 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Rejoindre le Serveur
                            <ArrowRight className="w-6 h-6" />
                        </motion.button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default BlogPage;